// F1 Data Dashboard Component
import React from 'react';
import { useF1Data } from '../../hooks/useSiblingData';
import { BarChart, LineChart, PieChart } from '../charts';

const F1Dashboard = () => {
  const { data, loading, error, refetch } = useF1Data();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 theme-secondary">Loading F1 data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="theme-primary text-xl mb-4">Error loading F1 data</div>
        <p className="theme-secondary mb-4">{error}</p>
        <button 
          onClick={refetch}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:theme-bg"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-8">No F1 data available</div>;
  }

  return (
    <div className="f1-dashboard space-y-8">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold theme-primary">F1 Data Dashboard</h1>
            <p className="theme-secondary mt-1">
              Racing statistics and analytics from F1 data
            </p>
          </div>
          <button 
            onClick={refetch}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:theme-bg flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {data.drivers && (
          <div className="card">
            <h3 className="text-lg font-semibold theme-primary">Drivers</h3>
            <p className="text-3xl font-bold theme-primary">{data.drivers.length}</p>
          </div>
        )}
        {data.races && (
          <div className="card">
            <h3 className="text-lg font-semibold theme-primary">Races</h3>
            <p className="text-3xl font-bold theme-primary">{data.races.length}</p>
          </div>
        )}
        {data.teams && (
          <div className="card">
            <h3 className="text-lg font-semibold theme-primary">Teams</h3>
            <p className="text-3xl font-bold theme-primary">{data.teams.length}</p>
          </div>
        )}
        {data.circuits && (
          <div className="card">
            <h3 className="text-lg font-semibold theme-primary">Circuits</h3>
            <p className="text-3xl font-bold theme-primary">{data.circuits.length}</p>
          </div>
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {data.drivers && data.drivers.length > 0 && (
          <div className="theme-bg p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">Driver Nationalities</h3>
            <PieChart 
              data={data.drivers.slice(0, 6).map(driver => ({
                label: driver.nationality || driver.name,
                value: 1
              }))}
              title=""
              showLabels={true}
              showLegend={true}
            />
          </div>
        )}
        {data.races && data.races.length > 0 && (
          <div className="theme-bg p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">Race Distribution</h3>
            <BarChart 
              data={data.races.slice(0, 10).map(race => ({
                label: race.round || race.name,
                value: 1
              }))}
              title=""
              showLabels={true}
              animate={true}
            />
          </div>
        )}
      </div>

      {/* Data Tables Section */}
      <div className="space-y-6">
        {data.drivers && data.drivers.length > 0 && (
          <div className="theme-bg rounded-lg shadow-md">
            <div className="px-6 py-4 border-b theme-border-secondary">
              <h2 className="text-xl font-semibold theme-primary">Drivers</h2>
              <p className="text-sm theme-secondary mt-1">{data.drivers.length} drivers</p>
            </div>
            <div className="p-6">
              <p className="theme-secondary">Table view not available. {data.drivers.length} drivers loaded.</p>
            </div>
          </div>
        )}

        {data.races && data.races.length > 0 && (
          <div className="theme-bg rounded-lg shadow-md">
            <div className="px-6 py-4 border-b theme-border-secondary">
              <h2 className="text-xl font-semibold theme-primary">Races</h2>
              <p className="text-sm theme-secondary mt-1">{data.races.length} races</p>
            </div>
            <div className="p-6">
              <p className="theme-secondary">Table view not available. {data.races.length} races loaded.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default F1Dashboard;
